import { createFileRoute, Link } from "@tanstack/react-router";
import { ProductCard } from "#/components/ProductCard";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { type Product, useProducts } from "#/queries/products";
import { useAddToCart } from "#/store/cart";
import { useWishlistStore } from "#/store/wishlist";

export const Route = createFileRoute("/account/wishlist")({
  head: () => ({ meta: [{ title: "Wishlist" }] }),
  component: WishlistTab,
});

function WishlistTab() {
  const productIds = useWishlistStore((s) => s.productIds);
  const { data, isPending } = useProducts(
    { ids: productIds, limit: productIds.length || 1 },
    { enabled: productIds.length > 0 },
  );
  const items = data?.items ?? [];

  return (
    <div>
      <h3 className="m-0 mb-6 font-bold font-display text-2xl text-lp-text">
        My Wishlist
      </h3>
      {productIds.length === 0 ? (
        <div className="py-16 text-center font-display text-lp-text-lt">
          Your wishlist is empty.{" "}
          <Link
            to={ROUTES.shop}
            className="text-lp-green-dk underline hover:text-lp-green-mid"
          >
            Add some books
          </Link>
          .
        </div>
      ) : isPending ? (
        <div
          role="status"
          aria-label="Loading wishlist"
          className="py-16 text-center font-display text-lp-text-lt"
        >
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center font-display text-lp-text-lt">
          We couldn&rsquo;t find your wishlist items.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
          {items.map((p) => (
            <WishlistTile key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function WishlistTile({ product }: { product: Product }) {
  const removeProduct = useWishlistStore((s) => s.removeProduct);
  const addToCart = useAddToCart();
  const defaultVariantId = product.variants?.[0]?.id;

  const onMove = () => {
    if (!defaultVariantId) {
      notify.error("This item has no available variant");
      return;
    }
    addToCart.mutate(
      { variant_id: defaultVariantId, quantity: 1 },
      {
        onSuccess: () => {
          removeProduct(product.id);
          notify.success("Moved to cart");
        },
        onError: () => notify.error("Couldn't add to cart"),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <ProductCard
        product={product}
        brandName={product.collection?.title ?? ""}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onMove}
          disabled={!defaultVariantId || addToCart.isPending}
          className="flex-1 cursor-pointer rounded-full border-0 bg-lp-green-mid px-4 py-2 font-display font-semibold text-sm text-white hover:bg-lp-green-dk disabled:cursor-not-allowed disabled:opacity-50"
        >
          {addToCart.isPending ? "Moving…" : "Move to cart"}
        </button>
        <button
          type="button"
          onClick={() => removeProduct(product.id)}
          aria-label="Remove from wishlist"
          className="cursor-pointer rounded-full border-2 border-lp-grey/60 bg-white px-3 py-2 font-display text-lp-text-lt text-sm hover:border-lp-green-mid hover:text-lp-green-dk"
        >
          Remove
        </button>
      </div>
    </div>
  );
}
